/**
 * Tab Visão Geral — painel consolidado do projeto.
 * Inspirado no Gestor_Projetos: stats grandes no topo, gráfico de trilhas,
 * barra de cronograma compacta.
 */

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import StatCard from '../../../components/StatCard';
import DonutChart from '../../../components/DonutChart';
import {
  TrendingUp, AlertTriangle, Clock, CheckCircle2, Hourglass,
  Users, Crown, Briefcase, UserCheck, Target,
} from 'lucide-react';
import ModalCampos from '../ModalCampos';
import { projetosAPI } from '../../../services/inovacaoAPI';
import { mensagemErro } from '../labels';

// Definição de pronto / critério de aceite — a âncora do encerramento (do template).
function CriterioAceite({ projeto, recarregar }) {
  const [editar, setEditar] = useState(false);
  const tem = !!projeto.criterio_aceite;
  async function salvar(vals) {
    try {
      await projetosAPI.atualizar(projeto.id, { criterio_aceite: vals.criterio_aceite || null });
      setEditar(false);
      toast.success('Definição de pronto atualizada');
      if (recarregar) recarregar();
    } catch (e) { toast.error(mensagemErro(e, 'Erro ao salvar')); }
  }
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  marginBottom: 16, borderLeft: `4px solid ${tem ? '#27ae60' : '#f39c12'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <h4 style={{ margin: 0, fontSize: '0.78rem', color: '#7f8c8d', textTransform: 'uppercase',
                     letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Target size={14} /> Definição de pronto (critério de aceite)
        </h4>
        <button onClick={() => setEditar(true)}
                style={{ background: 'none', border: 'none', color: 'var(--brand, #20a490)', cursor: 'pointer', fontSize: '0.82rem' }}>
          {tem ? 'editar' : 'definir'}
        </button>
      </div>
      <div style={{ marginTop: 8, color: tem ? '#2c3e50' : '#bdc3c7', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
        {tem ? projeto.criterio_aceite
             : 'Ainda não definido. O que precisa ser verdade para considerar o projeto entregue? É a âncora do encerramento.'}
      </div>
      {editar && (
        <ModalCampos titulo="Definição de pronto"
          descricao="O que precisa estar entregue para o projeto ser aceito."
          campos={[{ nome: 'criterio_aceite', label: 'Critério de aceite', tipo: 'textarea' }]}
          valoresIniciais={{ criterio_aceite: projeto.criterio_aceite || '' }}
          confirmLabel="Salvar" onConfirm={salvar} onCancel={() => setEditar(false)} />
      )}
    </div>
  );
}

export default function VisaoGeral({ projeto, fases, recarregar }) {
  const metrics = useMemo(() => {
    const naoBuffer = fases.filter((f) => !f.eh_buffer);
    const concluidas = naoBuffer.filter((f) => f.estado === 'concluida').length;
    const total = naoBuffer.length;
    const horasEst = naoBuffer.reduce((s, f) => s + parseFloat(f.horas_estimadas || 0), 0);
    const horasReais = naoBuffer.reduce((s, f) => s + parseFloat(f.horas_consumidas || 0), 0);
    const progresso = total > 0 ? Math.round((concluidas / total) * 100) : 0;
    const desvioH = horasReais - horasEst;
    const bufferTotal = parseFloat(projeto.buffer_horas_total || 0);
    const bufferCons = parseFloat(projeto.buffer_horas_consumidas || 0);
    const bufferPct = bufferTotal > 0 ? Math.round((bufferCons / bufferTotal) * 100) : 0;
    return { progresso, concluidas, total, horasEst, horasReais, desvioH, bufferTotal, bufferCons, bufferPct };
  }, [fases, projeto]);

  const distribuicao = useMemo(() => {
    const contagem = { planejada: 0, em_execucao: 0, concluida: 0, em_risco: 0, bloqueada: 0 };
    fases.filter((f) => !f.eh_buffer).forEach((f) => { contagem[f.estado] = (contagem[f.estado] || 0) + 1; });
    const total = Object.values(contagem).reduce((a, b) => a + b, 0) || 1;
    const cores = {
      planejada: '#95a5a6', em_execucao: '#3498db',
      concluida: '#27ae60', em_risco: '#e67e22', bloqueada: '#e74c3c',
    };
    return Object.entries(contagem)
      .filter(([, v]) => v > 0)
      .map(([estado, v]) => ({
        label: estado.replace('_', ' '),
        value: Math.round((v / total) * 100),
        color: cores[estado],
      }));
  }, [fases]);

  return (
    <div>
      <CriterioAceite projeto={projeto} recarregar={recarregar} />

      {/* Stakeholders */}
      <div style={{ background: '#fff', padding: 16, borderRadius: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: 16 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '0.78rem', color: '#7f8c8d',
                     textTransform: 'uppercase', letterSpacing: 0.4,
                     display: 'flex', alignItems: 'center', gap: 6 }}>
          <Users size={14} /> Stakeholders
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          <Stakeholder Icon={Briefcase} label="Setor solicitante"
                       valor={projeto.setor_solicitante?.nome} cor="#7f8c8d" />
          <Stakeholder Icon={Target} label="Setor beneficiário"
                       valor={projeto.setor_beneficiario?.nome || projeto.setor_solicitante?.nome}
                       cor="#27ae60"
                       extra={!projeto.setor_beneficiario ? '(mesmo que solicitante)' : null} />
          <Stakeholder Icon={Crown} label="Sponsor"
                       valor={projeto.sponsor?.nome} cor="#f39c12"
                       extra={!projeto.sponsor ? 'Não designado' : null} />
          <Stakeholder Icon={UserCheck} label="Responsável técnico"
                       valor={projeto.responsavel_tecnico?.nome} cor="#3498db" />
          <Stakeholder Icon={Users} label="Gerente da área"
                       valor={projeto.gerente_area?.nome} cor="#9b59b6" />
        </div>
      </div>

      {/* Stats principais */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard
          title="Progresso geral"
          value={`${metrics.progresso}%`}
          subtitle={`${metrics.concluidas}/${metrics.total} fases concluídas`}
          icon={<CheckCircle2 size={24} />}
          color="var(--brand, #20a490)"
        />
        <StatCard
          title="Horas realizadas"
          value={`${metrics.horasReais.toFixed(1)}h`}
          subtitle={`de ${metrics.horasEst.toFixed(1)}h estimadas`}
          icon={<Clock size={24} />}
          color="#3498db"
        />
        <StatCard
          title="Desvio de esforço"
          value={`${metrics.desvioH >= 0 ? '+' : ''}${metrics.desvioH.toFixed(1)}h`}
          subtitle={metrics.desvioH > 0 ? 'consumindo buffer' : 'dentro do plano'}
          icon={<TrendingUp size={24} />}
          color={metrics.desvioH > 0 ? '#e67e22' : '#27ae60'}
        />
        <StatCard
          title="Buffer consumido"
          value={`${metrics.bufferPct}%`}
          subtitle={`${metrics.bufferCons}/${metrics.bufferTotal}h`}
          icon={<Hourglass size={24} />}
          color={metrics.bufferPct > 50 ? '#e74c3c' : '#f39c12'}
        />
      </div>

      {/* Segunda linha: donut + cronograma compacto */}
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, marginBottom: 24 }}>
        <div style={cardStyle}>
          <h3 style={cardTitle}>Distribuição por estado</h3>
          {distribuicao.length > 0 ? (
            <DonutChart data={distribuicao} size={160}
                        centerText={`${metrics.total}`}
                        centerSubtext="fases" />
          ) : (
            <p style={{ color: '#bdc3c7', textAlign: 'center', padding: 20 }}>Sem dados.</p>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={cardTitle}>Cronograma compacto</h3>
          <BarraCronograma projeto={projeto} fases={fases} />
        </div>
      </div>

      {/* Alerta de risco se buffer > 50% */}
      {metrics.bufferPct > 50 && (
        <div style={{
          background: '#fef3e8', border: '1px solid #f39c12', borderRadius: 8,
          padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <AlertTriangle size={22} color="#e67e22" />
          <div>
            <b style={{ color: '#c0392b' }}>Atenção: buffer consumido acima de 50%.</b>
            <div style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>
              O projeto está em <b>{projeto.estado}</b>. Revisar cronograma é recomendado.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stakeholder({ Icon, label, valor, cor, extra }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div style={{ flexShrink: 0, color: cor, marginTop: 2 }}>
        <Icon size={16} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.7rem', color: '#95a5a6', textTransform: 'uppercase',
                      letterSpacing: 0.3, fontWeight: 600 }}>{label}</div>
        <div style={{ color: valor ? '#2c3e50' : '#bdc3c7', fontSize: '0.9rem',
                      fontWeight: valor ? 500 : 400 }}>
          {valor || '—'}
        </div>
        {extra && (
          <div style={{ fontSize: '0.72rem', color: '#95a5a6', marginTop: 1 }}>{extra}</div>
        )}
      </div>
    </div>
  );
}


function BarraCronograma({ projeto, fases }) {
  const inicio = projeto.data_inicio_planejada;
  const fim = projeto.data_fim_planejada;
  if (!inicio || !fim) return <p style={{ color: '#bdc3c7' }}>Cronograma ainda não calculado.</p>;

  const inicioD = new Date(inicio);
  const fimD = new Date(fim);
  const totalDias = Math.max(1, Math.round((fimD - inicioD) / 86400000));
  const hoje = new Date();
  const diasPassados = Math.max(0, Math.min(totalDias, Math.round((hoje - inicioD) / 86400000)));
  const hojePct = (diasPassados / totalDias) * 100;

  const coresEstado = {
    planejada: '#bdc3c7', em_execucao: '#3498db',
    concluida: '#27ae60', em_risco: '#e67e22', bloqueada: '#e74c3c',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#7f8c8d', marginBottom: 4 }}>
        <span>{inicio}</span>
        <span>{fim}</span>
      </div>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        {fases.map((f) => {
          if (!f.data_inicio_planejada || !f.data_fim_planejada) return null;
          const fi = new Date(f.data_inicio_planejada);
          const ff = new Date(f.data_fim_planejada);
          const left = Math.max(0, (fi - inicioD) / 86400000) / totalDias * 100;
          const width = Math.max(1, ((ff - fi) / 86400000 + 1) / totalDias * 100);
          return (
            <div key={f.id} title={`${f.nome} (${f.estado})`}
                 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{ width: 140, fontSize: '0.75rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            color: '#555' }}>
                {f.eh_buffer ? '🔸 ' : ''}{f.nome}
              </div>
              <div style={{ flex: 1, position: 'relative', height: 12, background: '#ecf0f1', borderRadius: 3 }}>
                <div style={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: `${left}%`, width: `${width}%`,
                  background: f.eh_buffer ? 'repeating-linear-gradient(45deg, #f39c12 0 6px, #e67e22 6px 12px)' : coresEstado[f.estado],
                  borderRadius: 3,
                }} />
              </div>
            </div>
          );
        })}
        {/* linha do "hoje" */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: `calc(140px + 8px + ((100% - 140px - 8px) * ${hojePct} / 100))`,
          width: 2, background: '#e74c3c', opacity: 0.5,
        }} />
      </div>
      <div style={{ fontSize: '0.75rem', color: '#95a5a6' }}>
        Hoje: <b>{Math.round(hojePct)}%</b> do cronograma decorrido
      </div>
    </div>
  );
}

const cardStyle = {
  background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
};
const cardTitle = { margin: '0 0 16px', fontSize: '1rem', color: '#2c3e50' };
